import { useEffect, useState } from 'react'
import { API_BASE } from '../lib/api'

export default function BlogsPanel({ adminToken }) {
	const [blogs, setBlogs] = useState([])
	const [title, setTitle] = useState('')
	const [excerpt, setExcerpt] = useState('')
	const [content, setContent] = useState('')
	const [imageUrl, setImageUrl] = useState('')
	const [author, setAuthor] = useState('')
	const [tags, setTags] = useState('')
	const [isPublished, setIsPublished] = useState(true)
	const [uploading, setUploading] = useState(false)
	const [saving, setSaving] = useState(false)
	const [status, setStatus] = useState('')

	useEffect(() => {
		if (!adminToken) return

		let mounted = true
		async function loadBlogs() {
			try {
				const res = await fetch(`${API_BASE}/admin/blogs`, {
					headers: { Authorization: `Bearer ${adminToken}` },
				})
				const data = await res.json()
				if (!res.ok) throw new Error(data.message || 'Failed to load blogs.')
				if (mounted) setBlogs(data)
			} catch (err) {
				if (mounted) setStatus(err.message)
			}
		}

		loadBlogs()
		return () => { mounted = false }
	}, [adminToken])

	async function handleImageUpload(e) {
		const file = e.target.files?.[0]
		if (!file) return

		setUploading(true)
		setStatus('')
		try {
			const formData = new FormData()
			formData.append('image', file)
			const res = await fetch(`${API_BASE}/admin/upload-blog-image`, {
				method: 'POST',
				headers: { Authorization: `Bearer ${adminToken}` },
				body: formData,
			})
			const data = await res.json()
			if (!res.ok) throw new Error(data.message || 'Blog image upload failed.')
			setImageUrl(data.url)
			setStatus('Blog image uploaded successfully.')
		} catch (err) {
			setStatus(err.message)
		} finally {
			setUploading(false)
			e.target.value = ''
		}
	}

	async function handleCreateBlog(e) {
		e.preventDefault()
		setSaving(true)
		setStatus('')

		try {
			const res = await fetch(`${API_BASE}/admin/blogs`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${adminToken}`,
				},
				body: JSON.stringify({
					title,
					excerpt,
					content,
					imageUrl,
					author,
					tags,
					isPublished,
				}),
			})
			const data = await res.json()
			if (!res.ok) throw new Error(data.message || 'Failed to create blog.')

			setBlogs(prev => [data, ...prev])
			setTitle('')
			setExcerpt('')
			setContent('')
			setImageUrl('')
			setAuthor('')
			setTags('')
			setIsPublished(true)
			setStatus('Blog published successfully.')
		} catch (err) {
			setStatus(err.message)
		} finally {
			setSaving(false)
		}
	}

	return (
		<section className="rounded-3xl border border-orange-100 bg-white/80 p-6 shadow-sm">
			<h2 className="title-font text-xl font-bold text-gray-900">Publish Blog</h2>
			<form onSubmit={handleCreateBlog} className="mt-4 space-y-3">
				<input value={title} onChange={e => setTitle(e.target.value)} required placeholder="Blog title" className="w-full rounded-xl border border-orange-200 bg-white px-4 py-2 text-sm" />
				<input value={author} onChange={e => setAuthor(e.target.value)} placeholder="Author (optional)" className="w-full rounded-xl border border-orange-200 bg-white px-4 py-2 text-sm" />
				<input value={tags} onChange={e => setTags(e.target.value)} placeholder="Tags (comma separated)" className="w-full rounded-xl border border-orange-200 bg-white px-4 py-2 text-sm" />
				<textarea value={excerpt} onChange={e => setExcerpt(e.target.value)} placeholder="Short excerpt" rows={2} className="w-full rounded-xl border border-orange-200 bg-white px-4 py-2 text-sm" />
				<textarea value={content} onChange={e => setContent(e.target.value)} required placeholder="Full blog content" rows={6} className="w-full rounded-xl border border-orange-200 bg-white px-4 py-2 text-sm" />

				<div className="rounded-2xl border border-orange-100 bg-orange-50/40 p-3">
					<p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Upload Blog Image To Blob</p>
					<label className="mt-2 inline-flex cursor-pointer items-center rounded-full bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-black">
						<input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
						{uploading ? 'Uploading...' : 'Choose Image'}
					</label>
					{imageUrl && <p className="mt-2 truncate text-xs text-gray-600">{imageUrl}</p>}
				</div>

				<label className="inline-flex items-center gap-2 text-sm text-gray-700">
					<input type="checkbox" checked={isPublished} onChange={e => setIsPublished(e.target.checked)} />
					Publish immediately
				</label>

				<button type="submit" disabled={saving} className="rounded-full bg-gradient-to-r from-red-500 to-orange-500 px-5 py-2 text-sm font-semibold text-white hover:from-red-600 hover:to-orange-600 disabled:opacity-60">
					{saving ? 'Saving...' : 'Create Blog'}
				</button>
				{status && <p className="text-sm font-medium text-gray-700">{status}</p>}
			</form>

			<div className="mt-6 border-t border-orange-100 pt-4">
				<h3 className="text-sm font-bold uppercase tracking-[0.18em] text-gray-500">Recent Blogs</h3>
				<div className="mt-3 space-y-2">
					{blogs.length === 0 ? (
						<p className="text-sm text-gray-500">No blogs yet.</p>
					) : (
						blogs.slice(0, 8).map(blog => (
							<div key={blog.id} className="rounded-xl border border-gray-200 bg-white px-3 py-2">
								<p className="text-sm font-semibold text-gray-900">{blog.title}</p>
								<p className="text-xs text-gray-500">{blog.isPublished ? 'Published' : 'Draft'} • {blog.slug}</p>
							</div>
						))
					)}
				</div>
			</div>
		</section>
	)
}
